/**
 * useBookmarks Hook
 * NIP-51 Bookmark management
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchBookmarks, toggleBookmark as toggleBookmarkService } from '../services/listService'
import { useAuth } from '../contexts/AuthContext'

interface UseBookmarksResult {
  bookmarks: string[]
  isLoading: boolean
  isBookmarked: (eventId: string) => boolean
  toggleBookmark: (eventId: string) => Promise<boolean>
  refetch: () => void
}

export function useBookmarks(): UseBookmarksResult {
  const { pubkey, sk } = useAuth()
  const [bookmarks, setBookmarks] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const refetch = useCallback(() => {
    setRefreshKey(prev => prev + 1)
  }, [])

  useEffect(() => {
    if (!pubkey) {
      setBookmarks([])
      return
    }

    setIsLoading(true)

    fetchBookmarks(pubkey)
      .then(setBookmarks)
      .catch(console.error)
      .finally(() => setIsLoading(false))
  }, [pubkey, refreshKey])

  const isBookmarked = useCallback((eventId: string) => {
    return bookmarks.includes(eventId)
  }, [bookmarks])

  const toggleBookmark = useCallback(async (eventId: string): Promise<boolean> => {
    if (!pubkey) {
      console.warn('Cannot toggle bookmark: not logged in')
      return false
    }

    // Optimistic update
    const wasBookmarked = bookmarks.includes(eventId)
    setBookmarks(prev => 
      wasBookmarked 
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId]
    )

    try {
      const result = await toggleBookmarkService(eventId, sk)
      
      if (!result.success) {
        // Revert optimistic update
        setBookmarks(prev =>
          wasBookmarked
            ? [...prev, eventId]
            : prev.filter(id => id !== eventId)
        )
        console.error('Toggle bookmark failed:', result.error)
        return wasBookmarked
      }

      return result.isBookmarked
    } catch (error) {
      // Revert optimistic update
      setBookmarks(prev =>
        wasBookmarked
          ? [...prev, eventId]
          : prev.filter(id => id !== eventId)
      )
      console.error('Toggle bookmark error:', error)
      return wasBookmarked
    }
  }, [pubkey, sk, bookmarks])

  return { bookmarks, isLoading, isBookmarked, toggleBookmark, refetch }
}

/**
 * useSingleBookmark Hook
 * Check and toggle bookmark for a single entry
 */
export function useSingleBookmark(eventId: string) {
  const { bookmarks, isLoading, toggleBookmark: toggle } = useBookmarks()
  
  const isBookmarked = bookmarks.includes(eventId)
  
  const toggleBookmark = useCallback(async () => {
    return toggle(eventId)
  }, [toggle, eventId])

  return { isBookmarked, isLoading, toggleBookmark }
}
